import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getOnlineOrderInvoiceSettingsApi,
  updateOnlineOrderInvoiceSettingsApi,
  getOnlineOrderCalendarApi,
  getOnlineOrderInvoiceSummaryApi,
  getOnlineOrderInvoiceApi,
  getOnlineOrderAccountStatementApi,
  getOnlineOrderOrdersApi,
  getInvoiceSettingsApi,
} from "@/api/online-ordering/invoice-statement";

// Thunks

// Fetch online order invoice settings
export const fetchOnlineOrderSettings = createAsyncThunk(
  "onlineOrderSettings/fetchSettings",
  async (restaurantId, { rejectWithValue }) => {
    const res = await getOnlineOrderInvoiceSettingsApi(restaurantId);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch settings");
  }
);

// Update online order invoice settings
export const updateOnlineOrderSettings = createAsyncThunk(
  "onlineOrderSettings/updateSettings",
  async ({ restaurantId, data }, { rejectWithValue }) => {
    const res = await updateOnlineOrderInvoiceSettingsApi(restaurantId, data);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to update settings");
  }
);

// Fetch online order calendar
export const fetchOnlineOrderCalendar = createAsyncThunk(
  "onlineOrderSettings/fetchCalendar",
  async ({ restaurantId, year }, { rejectWithValue }) => {
    const res = await getOnlineOrderCalendarApi(restaurantId, year);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch calendar");
  },
  {
    condition: (_, { getState }) => {
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.calendarLoading) return false;
      return true;
    },
  }
);

// Fetch online order invoice summary
export const fetchOnlineOrderInvoiceSummary = createAsyncThunk(
  "onlineOrderSettings/fetchSummary",
  async ({ restaurantId, invoiceId }, { rejectWithValue }) => {
    const res = await getOnlineOrderInvoiceSummaryApi(restaurantId, invoiceId);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch summary");
  },
  {
    condition: (_, { getState }) => {
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.summaryLoading) return false;
      return true;
    },
  }
);

// Fetch single online order invoice
export const fetchOnlineOrderInvoice = createAsyncThunk(
  "onlineOrderSettings/fetchInvoice",
  async ({ restaurantId, invoiceId }, { rejectWithValue }) => {
    const res = await getOnlineOrderInvoiceApi(restaurantId, invoiceId);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch invoice");
  },
  {
    condition: (_, { getState }) => {
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.invoiceLoading) return false;
      return true;
    },
  }
);

// Fetch invoice settings (bank details etc.)
export const fetchInvoiceSettings = createAsyncThunk(
  "onlineOrderSettings/fetchInvoiceSettings",
  async (arg, { rejectWithValue }) => {
    try {
      const restaurantId = typeof arg === "object" ? arg?.restaurantId : arg;
      if (!restaurantId) {
        return rejectWithValue("Restaurant ID is required to fetch invoice settings");
      }
      const res = await getInvoiceSettingsApi(restaurantId);
      if (
        res?.status === "success" ||
        res?.invoice_setting ||
        res?.data?.invoice_setting ||
        res?.data
      ) {
        return (
          res?.data?.invoice_setting ||
          res?.invoice_setting ||
          res?.data ||
          res
        );
      }
      return rejectWithValue(res?.message || "Failed to fetch invoice settings");
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch invoice settings"
      );
    }
  },
  {
    condition: (arg, { getState }) => {
      const targetId = typeof arg === "object" ? arg?.restaurantId : arg;
      if (!targetId) return false;
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.invoiceSettingsLoading) return false;
      if (state?.invoiceSettings) return false;
      return true;
    },
  }
);

// Fetch account statement
export const fetchOnlineOrderAccountStatement = createAsyncThunk(
  "onlineOrderSettings/fetchAccountStatement",
  async ({ restaurantId, invoiceId }, { rejectWithValue }) => {
    const res = await getOnlineOrderAccountStatementApi(restaurantId, invoiceId);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch account statement");
  },
  {
    condition: (_, { getState }) => {
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.accountStatementLoading) return false;
      return true;
    },
  }
);

// Fetch orders list (with pagination & search)
export const fetchOnlineOrderOrders = createAsyncThunk(
  "onlineOrderSettings/fetchOrders",
  async ({ restaurantId, invoiceId, params }, { rejectWithValue }) => {
    const res = await getOnlineOrderOrdersApi(restaurantId, invoiceId, params);
    if (res?.status === "success") return res.data;
    return rejectWithValue(res?.message || "Failed to fetch orders");
  },
  {
    condition: (_, { getState }) => {
      const state =
        getState()?.onlineOrdering?.onlineOrderSettings ||
        getState()?.onlineOrderSettings;
      if (state?.ordersLoading) return false;
      return true;
    },
  }
);

// Initial State
const initialState = {
  settings: null,
  calendar: null,
  summary: null,
  invoice: null,
  invoiceSettings: null,
  accountStatement: null,
  orders: null,
  settingsLoading: false,
  settingsUpdateLoading: false,
  calendarLoading: false,
  summaryLoading: false,
  invoiceLoading: false,
  invoiceSettingsLoading: false,
  accountStatementLoading: false,
  ordersLoading: false,
  success: null,
  error: null,
};

// Slice
const invoiceStatementSlice = createSlice({
  name: "onlineOrderSettings",
  initialState,
  reducers: {
    clearOnlineOrderMessages: (state) => {
      state.success = null;
      state.error = null;
    },
    clearOnlineOrderSummary: (state) => {
      state.summary = null;
    },
    clearOnlineOrderInvoice: (state) => {
      state.invoice = null;
    },
    clearOnlineOrderAccountStatement: (state) => {
      state.accountStatement = null;
    },
    clearOnlineOrderOrders: (state) => {
      state.orders = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchOnlineOrderSettings.pending, (state) => {
        state.settingsLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchOnlineOrderSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload;
      })
      // Update Settings
      .addCase(updateOnlineOrderSettings.pending, (state) => {
        state.settingsUpdateLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateOnlineOrderSettings.fulfilled, (state, action) => {
        state.settingsUpdateLoading = false;
        state.settings = action.payload;
        state.success = "Settings updated successfully";
      })
      .addCase(updateOnlineOrderSettings.rejected, (state, action) => {
        state.settingsUpdateLoading = false;
        state.error = action.payload;
      })
      // Fetch Calendar
      .addCase(fetchOnlineOrderCalendar.pending, (state) => {
        state.calendarLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderCalendar.fulfilled, (state, action) => {
        state.calendarLoading = false;
        state.calendar = action.payload;
      })
      .addCase(fetchOnlineOrderCalendar.rejected, (state, action) => {
        state.calendarLoading = false;
        state.error = action.payload;
      })
      // Fetch Summary
      .addCase(fetchOnlineOrderInvoiceSummary.pending, (state) => {
        state.summaryLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderInvoiceSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchOnlineOrderInvoiceSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload;
      })
      // Fetch Invoice
      .addCase(fetchOnlineOrderInvoice.pending, (state) => {
        state.invoiceLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderInvoice.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        state.invoice = action.payload;
      })
      .addCase(fetchOnlineOrderInvoice.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.error = action.payload;
      })
      // Fetch Invoice Settings
      .addCase(fetchInvoiceSettings.pending, (state) => {
        state.invoiceSettingsLoading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceSettings.fulfilled, (state, action) => {
        state.invoiceSettingsLoading = false;
        state.invoiceSettings = action.payload || null;
      })
      .addCase(fetchInvoiceSettings.rejected, (state, action) => {
        state.invoiceSettingsLoading = false;
        state.error = action.payload;
        state.invoiceSettings = null;
      })
      // Fetch Account Statement
      .addCase(fetchOnlineOrderAccountStatement.pending, (state) => {
        state.accountStatementLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderAccountStatement.fulfilled, (state, action) => {
        state.accountStatementLoading = false;
        state.accountStatement = action.payload;
      })
      .addCase(fetchOnlineOrderAccountStatement.rejected, (state, action) => {
        state.accountStatementLoading = false;
        state.error = action.payload;
      })
      // Fetch Orders
      .addCase(fetchOnlineOrderOrders.pending, (state) => {
        state.ordersLoading = true;
        state.error = null;
      })
      .addCase(fetchOnlineOrderOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload;
      })
      .addCase(fetchOnlineOrderOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearOnlineOrderMessages,
  clearOnlineOrderSummary,
  clearOnlineOrderInvoice,
  clearOnlineOrderAccountStatement,
  clearOnlineOrderOrders,
} = invoiceStatementSlice.actions;

export default invoiceStatementSlice.reducer;
